import { Token } from "@app/di/token";
import { BundleLoaderService } from "@app/services/bundle-loader.service";
import { Constructor, IMap } from "@tools";

export interface INgInjectResolve {
    name: string;
    dependencies?: Token[];
    fn?: Function;
}

export interface INgInjectArgs {
    token: Token;
    /**
     * inherit the dependencies and resolves of another object.
     * Useful for derived controllers.
     */
    inherit?: Token[];
    dependencies?: Token[];
    resolves?: INgInjectResolve[];
    libraries?: Token[];
}

export interface INgInject {
    /**
     * The angular type... like service, controller, directive, etc.
     */
    ngType: string;
    token: Token;
    dependencies: Token[];
    resolves?: INgInjectResolve[];
    construct: any[];
    resolveObject: { [name: string]: Function };
    libraries?: Token[];
    addedToApp: boolean;
}

export function NgService(args: INgInjectArgs) {
    return (target: Constructor<any>) => {
        DI.register("service", args, target);
    };
}
export function NgAnimation(args: INgInjectArgs) {
    return (target: Constructor<any>) => {
        DI.register("animation", args, target);
    };
}
export function NgComponent(args: INgInjectArgs) {
    return (target: Constructor<any>) => {
        DI.register("directive", args, target);
    };
}
export function NgInterceptor(args: INgInjectArgs) {
    return (target: Constructor<any>) => {
        DI.register("factory", args, target);
    };
}
export function NgFilter(args: INgInjectArgs) {
    return (target: Constructor<any>) => {
        DI.register("filter", args, target);
    };
}
export function NgView(args: INgInjectArgs) {
    return (target: Constructor<any>) => {
        DI.register("controller", args, target);
    };
}

export class DI {
    private static db: IMap<INgInject> = {};

    public static register(ngType: string, args: INgInjectArgs, constructor: Constructor<any>) {
        if (DI.db.hasOwnProperty(args.token.key)) {
            throw new Error(`A type with key ${args.token.key} has already been registered`);
        }
        args.dependencies = args.dependencies || [];
        args.resolves = args.resolves || [];
        args.libraries = args.libraries || [];

        if (args.inherit) {
            for (let baseToken of args.inherit) {
                let baseInject = DI.get(baseToken);
                if (baseInject.dependencies) args.dependencies.unshift(...baseInject.dependencies);
                if (baseInject.resolves) args.resolves.unshift(...baseInject.resolves);
                if (baseInject.libraries) args.libraries.unshift(...baseInject.libraries);
            }
        }

        let resolveObject = args.resolves &&
            args.resolves
                .reduce((p, c) => {
                    p[c.name] = [].concat(...c.dependencies.map(d => d.key)).concat(c.fn.bind(constructor));
                    return p;
                }, {});
        if (args.libraries) {
            resolveObject["scripts"] = [Token.BundleLoaderService.key, (bundleLoader: BundleLoaderService) => {
                return bundleLoader.load(args.libraries);
            }];
        }

        let ngInject = {
            token: args.token,
            ngType,
            dependencies: args.dependencies,
            resolves: args.resolves,
            construct: [].concat(args.dependencies.map(d => d.key)).concat((...a) => new constructor(...a)),
            resolveObject: resolveObject,
            libraries: args.libraries
        } as INgInject;
        DI.db[args.token.key] = ngInject;
    }

    public static get(token: Token): INgInject {
        return DI.db[token.key];
    }

    public static fillAngularApp(app: ng.IModule) {
        for (let key in DI.db) {
            let i = DI.db[key];
            if (!i.addedToApp) {
                app[i.ngType](i.token.key, i.construct);
                i.addedToApp = true;
            }    
        }
    }
}
